import { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Button } from '../../ui/button';
import {
  FileText,
  Search,
  Calendar,
  X,
  Loader2,
  Plus,
  Upload,
  Download,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react';
import { purchasingAPI, coreAPI, ContractPrice } from '../../../lib/api';
import SearchableSelect from '../../ui/SearchableSelect';
import { toast } from 'sonner';
import { Pagination } from '../../ui/pagination';
import { usePreferences } from '../../../contexts/PreferencesContext';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import ContractPriceModal from '../ContractPriceModal';
import ContractPriceDetailModal from '../ContractPriceDetailModal';
import ContractPriceImportModal from '../ContractPriceImportModal';

export default function ContractPricingTab() {
  const { formatDate } = usePreferences();
  const [contracts, setContracts] = useState<ContractPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Modal states
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editingContract, setEditingContract] = useState<ContractPrice | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Sorting
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pageSize = 50;

  // Text input states (for debouncing)
  const [textInputs, setTextInputs] = useState({
    contractNumber: '',
    supplier: '',
    customer: '',
    partNumber: '',
  });

  // Actual filter state (used for API calls)
  const [filters, setFilters] = useState({
    contractNumber: '',
    supplier: '',
    supplierGroup: '',
    customer: '',
    partNumber: '',
    currency: '',
    dateFrom: '',
    dateTo: '',
  });
  // CR-4593: Filter dropdown options
  const [supplierGroupOptions, setSupplierGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [supplierSiteOptions, setSupplierSiteOptions] = useState<{ value: string; label: string }[]>([]);
  const [companySiteOptions, setCompanySiteOptions] = useState<{ value: string; label: string }[]>([]);

  // Initialize
  useEffect(() => {
    setInitialLoadComplete(true);
    // CR-4593: Fetch filter options
    Promise.all([
      coreAPI.getCompanyGroups({ page_size: 200, company_type: 'supplier' }).catch(() => ({ data: { results: [] } })),
      coreAPI.getCompanies({ company_type: 'supplier', is_active: true, page_size: 500 }).catch(() => ({ data: { results: [] } })),
      coreAPI.getCompanies({ company_type: 'customer', is_active: true, page_size: 500 }).catch(() => ({ data: { results: [] } })),
    ]).then(([groupsRes, suppliersRes, customersRes]) => {
      const groups = (groupsRes.data as unknown as { results: { id: number; name: string }[] }).results || [];
      setSupplierGroupOptions(groups.map((g) => ({ value: String(g.id), label: g.name })));
      const suppliers = (suppliersRes.data as unknown as { results: { id: number; name: string }[] }).results || [];
      setSupplierSiteOptions(suppliers.map((s) => ({ value: String(s.id), label: s.name })));
      const customers = (customersRes.data as unknown as { results: { id: number; name: string }[] }).results || [];
      setCompanySiteOptions(customers.map((c) => ({ value: String(c.id), label: c.name })));
    });
  }, []);

  // Debounce text inputs
  useEffect(() => {
    if (!initialLoadComplete) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        contractNumber: textInputs.contractNumber,
        supplier: textInputs.supplier,
        customer: textInputs.customer,
        partNumber: textInputs.partNumber,
      }));
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [textInputs, initialLoadComplete]);

  useEffect(() => {
    if (initialLoadComplete) {
      fetchContracts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters, activeTab, initialLoadComplete]);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams: Record<string, string | number | boolean> = {
        ordering: '-created_at',
        page: currentPage,
        page_size: pageSize,
      };

      // Individual field filters
      if (filters.contractNumber) {
        queryParams['search'] = filters.contractNumber;
      }

      if (filters.supplier) {
        // CR-4593: ID-based filter now using SearchableSelect
        queryParams['supplier'] = filters.supplier;
      }

      if (filters.supplierGroup) {
        queryParams['supplier__group'] = filters.supplierGroup;
      }

      if (filters.customer) {
        // CR-4593: ID-based filter now using SearchableSelect
        queryParams['customer'] = filters.customer;
      }

      if (filters.partNumber) {
        queryParams['parts__part_number__icontains'] = filters.partNumber;
      }

      if (filters.currency) {
        queryParams['currency'] = filters.currency;
      }

      if (filters.dateFrom) {
        queryParams['valid_from__gte'] = filters.dateFrom;
      }
      if (filters.dateTo) {
        queryParams['valid_to__lte'] = filters.dateTo;
      }

      // Tab-based filtering
      if (activeTab === 'active') {
        queryParams['status_tab'] = 'active';
      } else if (activeTab === 'expired') {
        queryParams['status_tab'] = 'expired';
      } else if (activeTab === 'draft') {
        queryParams['status_tab'] = 'draft';
      }

      const response = await purchasingAPI.getContractPrices(queryParams);
      setContracts(response.data.results);
      setTotalCount(response.data.count);
      setTotalPages(Math.ceil(response.data.count / pageSize));
    } catch (err: unknown) {
      console.error('Error fetching contract prices:', err);
      setError(getApiErrorMessage(err, 'Failed to load contract prices'));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    if (['contractNumber', 'supplier', 'customer', 'partNumber'].includes(key)) {
      setTextInputs({ ...textInputs, [key]: value });
    } else {
      setFilters({ ...filters, [key]: value });
    }
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setTextInputs({
      contractNumber: '',
      supplier: '',
      customer: '',
      partNumber: '',
    });
    setFilters({
      contractNumber: '',
      supplier: '',
      supplierGroup: '',
      customer: '',
      partNumber: '',
      currency: '',
      dateFrom: '',
      dateTo: '',
    });
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="h-3 w-3 text-primary" />
    ) : (
      <ArrowDown className="h-3 w-3 text-primary" />
    );
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      draft: { label: 'Draft', className: 'bg-gray-100 text-gray-800' },
      active: { label: 'Active', className: 'bg-green-100 text-green-800' },
      expired: { label: 'Expired', className: 'bg-red-100 text-red-800' },
    };
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const handleRowClick = (contract: ContractPrice) => {
    setSelectedContractId(contract.id);
    setShowDetailModal(true);
  };

  const handleAddNew = () => {
    setEditingContract(null);
    setShowAddEditModal(true);
  };

  const handleEdit = (contract: ContractPrice) => {
    setEditingContract(contract);
    setShowAddEditModal(true);
  };

  const handleExport = async () => {
    try {
      const response = await purchasingAPI.exportContractPrices(filters);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contract_prices_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export completed successfully');
    } catch (err: unknown) {
      console.error('Export failed:', err);
      toast.error('Failed to export contract prices');
    }
  };

  const handleModalClose = () => {
    setShowAddEditModal(false);
    setEditingContract(null);
    fetchContracts();
  };

  const handleDetailModalClose = () => {
    setShowDetailModal(false);
    setSelectedContractId(null);
  };

  const handleImportComplete = () => {
    setShowImportModal(false);
    fetchContracts();
  };

  const sortedContracts = [...contracts].sort((a, b) => {
    if (!sortField) return 0;

    const aRaw: unknown = a[sortField as keyof ContractPrice];
    const bRaw: unknown = b[sortField as keyof ContractPrice];

    if (aRaw === null || aRaw === undefined) return 1;
    if (bRaw === null || bRaw === undefined) return -1;

    let aVal: string | number = String(aRaw);
    let bVal: string | number = String(bRaw);

    if (['valid_from', 'valid_to', 'created_at'].includes(sortField)) {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }

    if (['parts_count'].includes(sortField)) {
      aVal = parseInt(String(aVal));
      bVal = parseInt(String(bVal));
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading && !initialLoadComplete) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Loading contract pricing...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button
              onClick={fetchContracts}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters Panel */}
      {showFilters && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Filter Contract Prices</CardTitle>
              <button
                onClick={clearFilters}
                className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                <X className="h-3 w-3" />
                Clear All
              </button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {/* Contract Number */}
              <div>
                <label className="text-sm font-medium mb-2 block">Contract Number</label>
                <input
                  type="text"
                  placeholder="CP-001..."
                  value={textInputs.contractNumber}
                  onChange={(e) => handleFilterChange('contractNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Company Site */}
              <div>
                <label className="text-sm font-medium mb-2 block">Company Site</label>
                <SearchableSelect
                  options={companySiteOptions}
                  value={filters.customer}
                  onChange={(val) => setFilters((prev) => ({ ...prev, customer: val }))}
                  placeholder="All Company Sites"
                />
              </div>

              {/* Supplier Group */}
              <div>
                <label className="text-sm font-medium mb-2 block">Supplier Group</label>
                <SearchableSelect
                  options={supplierGroupOptions}
                  value={filters.supplierGroup}
                  onChange={(val) => setFilters((prev) => ({ ...prev, supplierGroup: val }))}
                  placeholder="All Supplier Groups"
                />
              </div>

              {/* Supplier Site */}
              <div>
                <label className="text-sm font-medium mb-2 block">Supplier Site</label>
                <SearchableSelect
                  options={supplierSiteOptions}
                  value={filters.supplier}
                  onChange={(val) => setFilters((prev) => ({ ...prev, supplier: val }))}
                  placeholder="All Supplier Sites"
                />
              </div>

              {/* Part Number */}
              <div>
                <label className="text-sm font-medium mb-2 block">Part Number</label>
                <input
                  type="text"
                  placeholder="Part number..."
                  value={textInputs.partNumber}
                  onChange={(e) => handleFilterChange('partNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Currency */}
              <div>
                <label className="text-sm font-medium mb-2 block">Currency</label>
                <select
                  value={filters.currency}
                  onChange={(e) => handleFilterChange('currency', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Currencies</option>
                  <option value="GBP">GBP</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="JPY">JPY</option>
                  <option value="CNY">CNY</option>
                </select>
              </div>

              {/* Valid From */}
              <div>
                <label className="text-sm font-medium mb-2 block">Valid From</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              {/* Valid To */}
              <div>
                <label className="text-sm font-medium mb-2 block">Valid To</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Table */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Contract Prices</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Showing {contracts.length} of {totalCount.toLocaleString()} contracts
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
                  <Search className="h-4 w-4 mr-2" />
                  Filters
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowImportModal(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </Button>
                <Button variant="outline" size="sm" onClick={handleExport}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <Button size="sm" onClick={handleAddNew}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Contract
                </Button>
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="active">Active</TabsTrigger>
                  <TabsTrigger value="expired">Expired</TabsTrigger>
                  <TabsTrigger value="draft">Draft</TabsTrigger>
                </TabsList>
              </div>
            </div>
          </CardHeader>

          <TabsContent value={activeTab} className="m-0">
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : contracts.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {Object.values(textInputs).some(v => v) || Object.values(filters).some(v => v)
                      ? 'No contracts match your filters'
                      : 'No contract prices found'}
                  </p>
                  {Object.values(textInputs).some(v => v) || Object.values(filters).some(v => v) ? (
                    <button onClick={clearFilters} className="mt-4 text-sm text-primary hover:underline">
                      Clear filters
                    </button>
                  ) : (
                    <Button className="mt-4" onClick={handleAddNew}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Contract
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {/* Top Pagination */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    itemsPerPage={pageSize}
                    onPageChange={handlePageChange}
                    itemLabel="contracts"
                  />

                  {/* CR-3866: Added max-height and sticky header support */}
                  <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-350px)]">
                    <table className="w-full" style={{ minWidth: '1200px' }}>
                      <thead className="sticky top-0 z-20">
                        <tr className="border-b bg-muted/80 backdrop-blur-sm">
                          <th
                            className="text-left py-3 px-4 font-medium cursor-pointer hover:bg-muted/50"
                            style={{ minWidth: '150px' }}
                            onClick={() => handleSort('contract_number')}
                          >
                            <div className="flex items-center gap-1">
                              Contract # {getSortIcon('contract_number')}
                            </div>
                          </th>
                          <th
                            className="text-left py-3 px-4 font-medium cursor-pointer hover:bg-muted/50"
                            style={{ minWidth: '180px' }}
                            onClick={() => handleSort('customer_name')}
                          >
                            <div className="flex items-center gap-1">
                              Customer {getSortIcon('customer_name')}
                            </div>
                          </th>
                          <th
                            className="text-left py-3 px-4 font-medium cursor-pointer hover:bg-muted/50"
                            style={{ minWidth: '180px' }}
                            onClick={() => handleSort('supplier_name')}
                          >
                            <div className="flex items-center gap-1">
                              Supplier {getSortIcon('supplier_name')}
                            </div>
                          </th>
                          <th
                            className="text-left py-3 px-4 font-medium cursor-pointer hover:bg-muted/50"
                            style={{ minWidth: '130px' }}
                            onClick={() => handleSort('valid_from')}
                          >
                            <div className="flex items-center gap-1">
                              Valid From {getSortIcon('valid_from')}
                            </div>
                          </th>
                          <th
                            className="text-left py-3 px-4 font-medium cursor-pointer hover:bg-muted/50"
                            style={{ minWidth: '130px' }}
                            onClick={() => handleSort('valid_to')}
                          >
                            <div className="flex items-center gap-1">
                              Valid To {getSortIcon('valid_to')}
                            </div>
                          </th>
                          <th
                            className="text-center py-3 px-4 font-medium"
                            style={{ minWidth: '100px' }}
                          >
                            Currency
                          </th>
                          <th
                            className="text-center py-3 px-4 font-medium cursor-pointer hover:bg-muted/50"
                            style={{ minWidth: '100px' }}
                            onClick={() => handleSort('status')}
                          >
                            <div className="flex items-center justify-center gap-1">
                              Status {getSortIcon('status')}
                            </div>
                          </th>
                          <th
                            className="text-center py-3 px-4 font-medium cursor-pointer hover:bg-muted/50"
                            style={{ minWidth: '80px' }}
                            onClick={() => handleSort('parts_count')}
                          >
                            <div className="flex items-center justify-center gap-1">
                              Parts {getSortIcon('parts_count')}
                            </div>
                          </th>
                          <th
                            className="text-left py-3 px-4 font-medium cursor-pointer hover:bg-muted/50"
                            style={{ minWidth: '140px' }}
                            onClick={() => handleSort('created_at')}
                          >
                            <div className="flex items-center gap-1">
                              Created {getSortIcon('created_at')}
                            </div>
                          </th>
                          <th className="text-center py-3 px-4 font-medium" style={{ minWidth: '80px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedContracts.map((contract) => (
                          <tr
                            key={contract.id}
                            className="border-b hover:bg-muted/50 cursor-pointer transition-colors"
                            onClick={() => handleRowClick(contract)}
                          >
                            <td className="py-3 px-4">
                              <span className="font-mono text-sm font-medium">{contract.contract_number}</span>
                            </td>
                            <td className="py-3 px-4 text-sm">{contract.customer_name || contract.customer_name || 'N/A'}</td>
                            <td className="py-3 px-4 text-sm">{contract.supplier_name || 'N/A'}</td>
                            <td className="py-3 px-4 text-sm">
                              {contract.valid_from ? formatDate(contract.valid_from) : '-'}
                            </td>
                            <td className="py-3 px-4 text-sm">
                              {contract.valid_to ? formatDate(contract.valid_to) : '-'}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="px-2 py-1 bg-slate-100 rounded text-xs font-medium">
                                {contract.currency}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              {getStatusBadge(contract.status)}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                                {contract.parts_count || 0}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-muted-foreground">
                              {formatDate(contract.created_at)}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="text-xs text-primary hover:underline">View →</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Bottom Pagination */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    itemsPerPage={pageSize}
                    onPageChange={handlePageChange}
                    itemLabel="contracts"
                  />
                </>
              )}
            </CardContent>
          </TabsContent>
        </Tabs>
      </Card>

      {/* Detail Modal */}
      {showDetailModal && selectedContractId && (
        <ContractPriceDetailModal
          contractId={selectedContractId}
          isOpen={showDetailModal}
          onClose={handleDetailModalClose}
          onEdit={handleEdit}
        />
      )}

      {/* Add/Edit Modal */}
      {showAddEditModal && (
        <ContractPriceModal
          isOpen={showAddEditModal}
          onClose={handleModalClose}
          contract={editingContract}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ContractPriceImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImportComplete={handleImportComplete}
        />
      )}
    </div>
  );
}
